import { unitOf, type SeatIdentity, type SideBrandingSet } from '@badger-sentinel/protocol';

import { sideOf } from '../../game/sides.js';
import { ImageSlot, SLOT } from '../../theme/imagery.js';
import { ThemeToggleButton } from '../../game/chrome.js';
import { DATA_LABEL, LABEL } from '../../theme/type.js';
import type { BsTheme } from '../../theme/useTheme.js';

/**
 * The identity row (design 69–92).
 *
 * shell.md §Identity: sixteen people share a room and half of them are on the
 * other side, so **which team's screen this is has to be readable across that
 * room**, not just at arm's length. This is the one piece of chrome that should
 * be loud.
 *
 * ## It was not loud, and this row is the fix
 *
 * The band was 32px with a 20×20 crest and an 11px side name — smaller than the
 * body text — while `game/chrome.tsx` reserved an 84px header with a 62px crest
 * for entry and seat select, screens an operator looks at for four seconds. The
 * loud identity was on the disposable screens and the whisper was on the one
 * they sit at for ninety minutes; in a side-by-side screenshot BLUFOR and
 * REDFOR consoles were indistinguishable past about a metre.
 *
 * The band is now **52px**, and the twenty extra pixels are spent on exactly the
 * two things that carry at distance: the crest at `PLATE` and the side name at
 * 26px. Everything else in this row is arm's-length detail — the row's own
 * docstring has always said so — and it is *unchanged in size*, moved into a
 * compact cluster on the right rather than competing with the identity on a
 * single baseline. Growing the band without re-ranking what is in it would have
 * produced a bigger row that was still not readable.
 *
 * The twenty pixels come off the centre pane (`viewport.ts` carries the revised
 * arithmetic). Nothing enforces the old figure in code, and MW's timeline dock —
 * the module the floor was measured against — is 122px.
 *
 * Every string here comes from somewhere: the side's name and identity from the
 * scenario's `sides` block, the unit from `unitOf(side, cell)`, the operator and
 * the seat from `session.hello.seat`. A scenario that names no unit for a cell
 * renders no unit line rather than an invented one (M5-D10).
 *
 * ## The two app-level controls at the end of the row
 *
 * The theme toggle has been here since Phase 1 and LEAVE joins it in Phase 9.
 * Both belong in chrome because they have to be reachable whatever tab is open,
 * and a seat with no way out is a seat you close the browser on. The button is
 * not a seat *switcher*; Q19 defers that.
 *
 * **LEAVE asks first.** It used to be wired straight through to `onLeave`, so a
 * hand travelling to the theme toggle forty pixels away gave up the seat with
 * no question, mid-window. It now raises a `leave-seat` confirm that the shell
 * renders under this row, priced with the clock and the queue depth.
 */

export interface IdentityRowProps {
  readonly seat: SeatIdentity;
  readonly sides: SideBrandingSet;
  readonly scenario: string;
  readonly theme: BsTheme;
  readonly onToggleTheme: () => void;
  /** Raises the confirm. The shell owns the question and the answer. */
  readonly onRequestLeave: () => void;
  readonly leaveArmed: boolean;
}

/**
 * Both art plates, at one size.
 *
 * The side crest and the seat patch are the row's two pieces of imagery and they
 * read as a pair, so they are sized together rather than separately — the patch
 * was 24px against the crest's 40px and looked like a smaller version of the
 * same idea rather than the second half of it.
 */
const PLATE = 40;

/**
 * The art plate.
 *
 * `padding: PLATE / 14` and `boxSizing: border-box` are lifted from the seat
 * picker's own patch box (`SeatSelectScreen`), because these are the same object
 * on two screens and cloth pressed against its frame looks like a mistake in
 * both. One component so the crest and the patch cannot drift apart again — they
 * already had, at 40px and 24px.
 */
function Plate(props: { children: React.ReactNode }): JSX.Element {
  return (
    <span
      style={{
        flex: 'none',
        boxSizing: 'border-box',
        width: PLATE,
        height: PLATE,
        padding: Math.round(PLATE / 14),
        overflow: 'hidden',
        border: '1px solid var(--border-strong)',
        background: 'var(--surface-3)',
      }}
    >
      {props.children}
    </span>
  );
}

export function IdentityRow(props: IdentityRowProps): JSX.Element {
  const { seat, sides } = props;
  const side = sideOf(seat.team, sides);
  const unit = unitOf(sides[seat.team], seat.cell);

  return (
    <div
      data-mat="chrome"
      data-testid="identity-row"
      style={{
        flex: 'none',
        height: 52,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 12px',
        overflow: 'hidden',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--rule)',
      }}
    >
      {/*
        The distance pair. A 40px crest and a 26px name is the smallest
        combination that resolves across a room of sixteen laptops; the accent
        is the side's own, so the two teams differ in colour as well as in word.
      */}
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Plate>
          {/*
            A scenario that ships no crest for this side gets a plate in the
            side's own colour, not an empty box. At 20px the empty slot read as
            a broken image; at 40px it would have read as a hole. A solid block
            of team colour is the cheapest identity-at-distance there is, so the
            degraded case still does this row's job.
          */}
          <ImageSlot
            slot={SLOT.sideCrest(seat.team)}
            scenario={props.scenario}
            emptyFill={side.accent}
            compact
          />
        </Plate>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          {/*
            The console's `h1`, and the page had none at all — seventy focusable
            controls and no document outline, so a screen reader met the whole
            seat as one flat list with nothing to jump between. The side name is
            already the largest and first thing in the reading order and it is
            what the seat *is*; it only ever lacked the element that says so.
            Margin is zeroed because the size here is deliberate, not the
            browser's.
          */}
          <h1
            data-testid="side-name"
            style={{
              margin: 0,
              fontFamily: 'var(--font-ui)',
              fontSize: 26,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '.10em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              color: side.accent,
            }}
          >
            {side.name}
          </h1>
          <span
            style={{
              ...LABEL,
              fontSize: 12,
              letterSpacing: '.08em',
              color: 'var(--text-3)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {side.identity}
          </span>
        </span>
      </div>

      <span style={{ flex: 'none', width: 1, height: 28, background: 'var(--rule)' }} />

      {/* Arm's-length detail from here on, at the size it has always been. */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          overflow: 'hidden',
        }}
      >
        <span style={{ ...LABEL, fontSize: 12 }}>SCENARIO</span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            ...LABEL,
            fontSize: 14,
            color: 'var(--text-2)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {props.scenario}
        </span>
      </div>

      <div
        data-testid="seat"
        style={{
          flex: '0 1 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {seat.displayName !== null && (
          <span
            style={{
              ...LABEL,
              fontSize: 14,
              letterSpacing: '.08em',
              color: 'var(--text-2)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {seat.displayName}
          </span>
        )}

        {/*
          Seat over unit, the way side sits over formation at the other end of
          the row. The two clusters are the same shape mirrored — plate, then a
          stacked pair — so the row reads as two bookends rather than a crest at
          one end and a run of chips at the other. Smaller than the side pair,
          because which team this is has to carry across the room and which seat
          it is only has to carry to the person sitting at it.
        */}
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 3,
            minWidth: 0,
          }}
        >
          <span
            data-testid="seat-cell"
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '.10em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              color: side.accent,
            }}
          >
            {seat.cell}
          </span>
          {unit !== null && (
            <span
              style={{
                ...LABEL,
                fontSize: 12,
                letterSpacing: '.08em',
                color: 'var(--text-3)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {unit}
            </span>
          )}
        </span>

        <Plate>
          <ImageSlot
            slot={SLOT.seatPatch(seat.cell, seat.team)}
            scenario={props.scenario}
            emptyFill="var(--surface-4)"
            compact
          />
        </Plate>
      </div>

      <ThemeToggleButton theme={props.theme} onToggle={props.onToggleTheme} inline />

      <button
        type="button"
        data-testid="leave-seat"
        aria-expanded={props.leaveArmed}
        onClick={props.onRequestLeave}
        style={{
          flex: 'none',
          height: 24,
          padding: '0 8px',
          background: props.leaveArmed ? 'var(--red-surface)' : 'transparent',
          border: `1px solid ${props.leaveArmed ? 'var(--red)' : 'var(--border)'}`,
          color: props.leaveArmed ? 'var(--red-text)' : 'var(--text-muted)',
          ...DATA_LABEL,
          fontWeight: 400,
          cursor: 'pointer',
        }}
      >
        LEAVE
      </button>
    </div>
  );
}
